/* eslint-disable no-case-declarations */
import type { SectionValues, SectionValueItem, Article, ArticleMap } from '@/types';
import type { ExtraArticle, Tile } from '@/project';

import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import _ from 'lodash';

// Calculations
import { Underlagstak, TileFamily, TileFinishMap, TileFamilyMap, Material, MarketStr, RoofArticle, NockPannaVariant } from '@/calculation/common';

// stores
import pinia from '@/store/store';
import { useProjectStore } from '@/store/project';
import { useArticleStore } from '@/store/article';

import { AccessoryColor, ExtraAssortment, FacadeHeight, accessoryColorMap } from '@/enums';
import { getArtHash, getArtPrefix } from '@/helpers/utilities';
import { getRoofTile } from '@/helpers/tile';
import { useLoadingStore } from '@/store/loading';

const project = useProjectStore(pinia);
const { setLoading } = useLoadingStore(pinia);

const { calc_tile, selections, activeRoof } = storeToRefs(project);
const { getArticles, getArticlesByPrefix, getAccessories } = useArticleStore(pinia);

// Struktur för att beskriva taksäkerhetsartiklarna
type TileReq = TileFamily[];
type FasadHöjdReq = FacadeHeight[];
type TaklutningReq = {min: number, max: number};
type MarketReq = MarketStr[];
type GenericReq = boolean;

type Rule = {
  tileReq?: TileReq,
  fasadHöjdReq?: FasadHöjdReq,
  taklutningReq?: TaklutningReq,
  marketReq?: MarketReq,
  genericReq?: GenericReq,
  arts: string[],
  subRules?: Rule[]
};

function processRules(rules: Rule[], arts: string[], tileFamily: TileFamily,
                      facadeHeight: FacadeHeight, lutning: number, market: MarketStr) {
  for (const rule of rules) {
    // console.log(rule, tileFamily, facadeHeight, lutning);
    if (rule?.tileReq && !rule.tileReq.includes(tileFamily)) continue;
    if (rule?.fasadHöjdReq && !rule.fasadHöjdReq.includes(facadeHeight)) continue;
    if (rule?.taklutningReq && (lutning < rule.taklutningReq.min || lutning > rule.taklutningReq.max)) continue;
    if (rule?.marketReq && !rule.marketReq.includes(market)) continue;
    if (rule?.genericReq === false) continue;

    arts.push(...rule.arts);

    if (rule?.subRules) processRules(rule?.subRules, arts, tileFamily, facadeHeight, lutning, market);
  }
}

const getAssortments = async ({
  tile,
  angle,
  accessoryColor,
  baseroof,
  facadeHeight,
}: {
  tile: Tile;
  angle: number;
  accessoryColor: AccessoryColor;
  baseroof: Underlagstak;
  facadeHeight: FacadeHeight;
}): Promise<{ [key: string]: (string | { artnr: string; sort: string })[] }> => {
  const acc = {
    [ExtraAssortment.TakpannorOchTillbehor]: [],
    [ExtraAssortment.VentilationGenomforing]: [],
    [ExtraAssortment.TatningLuftning]: [],
    [ExtraAssortment.Taksakerhet]: [],
    [ExtraAssortment.Infastning]: [],
    [ExtraAssortment.OvrigaTillbehor]: [],
    [ExtraAssortment.Solar]: [],
  } as { [key: string]: (string | { artnr: string; sort: string })[] };
  const roofTile = getRoofTile(tile.family);

  for (const key of Object.keys(acc) as ExtraAssortment[]) {
    let excludeTileFamily = '';
    if (tile.family === TileFamily.HansaFalsatLertegel) {
      excludeTileFamily = TileFamilyMap[TileFamily.HansaFalsatLertegelRakFramkant];
    } else if (tile.family === TileFamily.TvillingFalsatLertegel) {
      excludeTileFamily = TileFamilyMap[TileFamily.TvillingLertegelRakFramkant];
    }

    const items = [] as (string | { artnr: string; sort: string })[];
    let articles = {} as ArticleMap;
    switch (key) {
      case ExtraAssortment.VentilationGenomforing:
        if (project.market != MarketStr.Tyskland) {
          items.push(...['060400', '060300', '060800']);
          switch (baseroof) {
            case Underlagstak.RåspontMedPapp:
              break;
            default:
              items.push('060000');
              break;
          }
        }
        break;
      case ExtraAssortment.TatningLuftning:
        if (project.market == MarketStr.Tyskland) {
          articles = await getAccessories({
            itemGroup: ['002'],
          }, true);
          // BENT-270: For articles that appear in multiple colors, only show the one that matches accessoryColor
          const prefixMap = Object.values(articles).map(a => getArtPrefix(a.ArtNr));
          items.push(...Object.values(articles).filter(a => {
            if (prefixMap.filter(p => p == getArtPrefix(a.ArtNr)).length > 1)
              return accessoryColorMap?.[accessoryColor]?.includes(a.ColorID);
            return true;
          }).filter(a => {
            if (['C92220', 'C97220'].includes(a.ArtNr))
              return tile.family == TileFamily.Carisma;
            return true;
          }).map((a) => a.ArtNr));
        } else {
          items.push(...['098002', '097200', '091990', '093110']);
          switch (accessoryColor) {
            case AccessoryColor.GalvanizedGreySilver:
              items.push(...['118490', '118520', '092620', '092220', '092320', '091820']);
              break;
            case AccessoryColor.Black:
              items.push(...['118420', '118520', '092620', '092220', '092320', '091820']);
              break;
            case AccessoryColor.BrickRed:
              items.push(...['118424', '118524', '092624', '092224', '092324', '091824']);
              break;
          }
          if (tile.family == TileFamily.Carisma) items.push('C92220', 'C97190');
        }

        // BENT-294
        const colorSuffix = accessoryColor === AccessoryColor.BrickRed ? '24' : '20';
        if ([MarketStr.Norge, MarketStr.Tyskland].includes(project.market)) {
          items.push('0924' + colorSuffix)
        } else if ([MarketStr.Sverige, MarketStr.Finland].includes(project.market)) {
          items.push(...['0916' + colorSuffix, '0903' + colorSuffix])
        }

        break;
      case ExtraAssortment.Infastning:
        items.push(...['096400', '096390', '113891']);
        break;
      case ExtraAssortment.OvrigaTillbehor:
        if (project.market == MarketStr.Tyskland)
          items.push(...['030620', '030624', '030820', '030824', '098700', '098600', '086224', '086227', '030520', '030524', '030720', '030724', '030900']);
        else
          items.push(...['030520', '030524', '030720', '030724', '030320', '030900']);

        let fetchArts = '0989';
        if (project.market !== MarketStr.Tyskland)
          fetchArts += ',H983';
        articles = await getArticlesByPrefix(fetchArts, true, {
          colorgroup: [TileFinishMap[tile.coating]],
          colornumber: [tile.color],
        });
        items.push(...Object.values(articles).map((a) => a.ArtNr));
        break;
      case ExtraAssortment.TakpannorOchTillbehor:
        // BENT-231: Only show products in certain product families depending on active tile
        const productFamilies = {
          [TileFamily.Palema]: ['01', '05', '06', '08', '21'],
          [TileFamily.Exklusiv]: ['02', '05', '06', '08', '22'],
          [TileFamily.Carisma]: ['03', '05', '07', '08', '23'],
          [TileFamily.HansaFalsatLertegel]: ['45', '50', '51', '55'],
          [TileFamily.HansaFalsatLertegelRakFramkant]: ['44', '50', '54'],
          [TileFamily.TvillingFalsatLertegel]: ['46', '50', '56'],
          [TileFamily.TvillingLertegelRakFramkant]: ['43', '50', '51', '53'],
          [TileFamily.PianoFalsatLertegel]: ['47', '50', '52', '57'],
          [TileFamily.Strängpressat2kupLertegel]: ['42', '50', '51'],
          [TileFamily.Mecklenburger]: ['04', '05', '06', '08', '24'],
          [TileFamily.None]: [],
        };

        articles = await getAccessories(
          {
            assortment: calc_tile.value.material === Material.Betong ? 'Betongtillbehör' : 'Lertegeltillbehör',
            colorgroup: [TileFinishMap[tile.coating]],
            colornumber: [tile.color],
            productFamilies: productFamilies?.[tile.family] ?? [],
            // tilefamily: TileFamilyMap[tile.family],
            // excludeTileFamily: [excludeTileFamily],
          },
          true,
        );
        items.push(...Object.values(articles).map((a) => a.ArtNr));
        articles = await getAccessories(
          {
            assortment: calc_tile.value.material === Material.Betong ? 'Betongtakpannor' : 'Lertegeltakpannor',
            colorgroup: [TileFinishMap[tile.coating]],
            colornumber: [tile.color],
            tilefamily: TileFamilyMap[tile.family],
            excludeTileFamily: [excludeTileFamily],
            productFamilies: productFamilies?.[tile.family] ?? [],
          },
          true,
        );
        items.push(...Object.values(articles).map((a) => a.ArtNr));

        const nockTile = roofTile.itemNos[tile.nock == NockPannaVariant.Standard ? RoofArticle.NockPanna : RoofArticle.NockPannaAlt];
        if (tile.family != TileFamily.None && roofTile?.variants?.[tile.coating]?.[tile.color] && getArtPrefix(nockTile) == '0300') {
          items.push({ artnr: '0310' + roofTile.variants?.[tile.coating]?.[tile.color]?.nr, sort: 'aaaa' });
        }
        break;
      case ExtraAssortment.Taksakerhet:
        // Fetch all safety articles w/ correct color-postfix
        articles = await getAccessories({
          assortment: 'Taksäkerhet',
          colornumber: accessoryColorMap[accessoryColor],
        });
        const availableArticles = Object.values(articles).map((a) => a?.ArtNr);

        const mainTile = roofTile.getTileVariant(RoofArticle.TakPanna, { color: tile.color, finish: tile.coating });
        const tileColorSuffix = mainTile?.substring(mainTile.length - 2, mainTile.length);

        // Determine which articles should be shown
        const whitelisted: string[] = [];

        const koppladeTakstegArtNo = roofTile.getArticle(RoofArticle.KoppladeTaksteg);

        // Rules for when which articles should appear in this section
        const rules: Rule[] = [
          {
            marketReq: [MarketStr.Sverige, MarketStr.Norge, MarketStr.Finland],
            arts: [],
            subRules: [
              {
                arts: [ '0701xx', '0813xx', '0814xx', '083990', '1300xx', '130190', '1308xx', '132900',
                  '1343xx', '1351xx', '136090', '1369xx', '1377xx', '1378xx', '1396xx', '1397xx', '1398xx' ],
              },
              {
                fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                arts: ['0817xx'],

                subRules: [{
                  taklutningReq: { min: 14, max: 45 },
                  arts: ['0712xx'],
                }],
              },
              {
                fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m, FacadeHeight.Less5m],
                arts: ['0729xx'],
              },
              {
                tileReq: [TileFamily.Palema],
                arts: ['1372xx'],

                subRules: [
                  {
                    marketReq: [MarketStr.Sverige, MarketStr.Finland],
                    arts: ['1348xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    taklutningReq: { min: 14, max: 45 },
                    arts: ['0762xx', '0724xx'],
                  },
                ],
              },
              {
                tileReq: [TileFamily.Exklusiv],
                arts: ['1371xx'],

                subRules: [
                  {
                    marketReq: [MarketStr.Sverige, MarketStr.Finland],
                    arts: ['1348xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    taklutningReq: { min: 14, max: 45 },
                    arts: ['0723xx'],
                  }
                ],
              },
              {
                tileReq: [TileFamily.Carisma],
                arts: ['C832xx'],
              },
              {
                tileReq: [TileFamily.Carisma, TileFamily.PianoFalsatLertegel],
                arts: ['171090', '171090-10', 'C1348xx'],
              },
              {
                tileReq: [TileFamily.HansaFalsatLertegel, TileFamily.HansaFalsatLertegelRakFramkant],
                arts: ['L1374xx'],

                subRules: [
                  {
                    marketReq: [MarketStr.Sverige, MarketStr.Finland],
                    arts: ['1348xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    taklutningReq: { min: 14, max: 45 },
                    arts: ['H763xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less5m, FacadeHeight.To58, FacadeHeight.More8m],
                    taklutningReq: { min: 14, max: 55 },
                    arts: ['H841xx', 'H831xx'],
                  },
                ],
              },
              {
                tileReq: [TileFamily.TvillingFalsatLertegel, TileFamily.TvillingLertegelRakFramkant],
                arts: [],

                subRules: [
                  {
                    marketReq: [MarketStr.Sverige, MarketStr.Finland],
                    arts: ['1348xx', 'L1375xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    taklutningReq: { min: 14, max: 45 },
                    arts: ['H764xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    arts: ['0828xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less5m, FacadeHeight.To58, FacadeHeight.More8m],
                    taklutningReq: { min: 14, max: 55 },
                    arts: ['H845xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less5m, FacadeHeight.To58, FacadeHeight.More8m],
                    taklutningReq: { min: 14, max: 65 },
                    arts: ['L825xx'],
                  },
                ],
              },
              {
                tileReq: [TileFamily.Strängpressat2kupLertegel],
                arts: ['L1348xx', 'L1372xx'],
              },
              {
                tileReq: [TileFamily.Palema, TileFamily.Carisma],
                fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                taklutningReq: { min: 14, max: 45 },
                arts: ['0722xx'],
              },
              {
                tileReq: [TileFamily.Palema, TileFamily.Carisma, TileFamily.PianoFalsatLertegel],
                fasadHöjdReq: [FacadeHeight.Less5m, FacadeHeight.To58, FacadeHeight.More8m],
                taklutningReq: { min: 14, max: 65 },
                arts: ['0833xx'],
              },
              {
                tileReq: [TileFamily.Palema, TileFamily.PianoFalsatLertegel],
                fasadHöjdReq: [FacadeHeight.Less5m, FacadeHeight.To58, FacadeHeight.More8m],
                taklutningReq: { min: 14, max: 55 },
                arts: ['0843xx'],
              },
              {
                tileReq: [TileFamily.Exklusiv, TileFamily.Carisma, TileFamily.Strängpressat2kupLertegel],
                arts: [],
                subRules: [
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    taklutningReq: { min: 14, max: 45 },
                    arts: ['0761xx', '0721xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                    taklutningReq: { min: 14, max: 65 },
                    arts: ['0831xx'],
                  },
                  {
                    fasadHöjdReq: [FacadeHeight.Less5m, FacadeHeight.To58, FacadeHeight.More8m],
                    taklutningReq: { min: 14, max: 55 },
                    arts: ['0841xx'],
                  },
                ],
              },
              {
                tileReq: [TileFamily.Palema],
                arts: ['1901xx'],
              },
              {
                tileReq: [TileFamily.Exklusiv],
                arts: ['1902xx'],
              },
              {
                tileReq: [TileFamily.Carisma],
                arts: ['C838xx'],
              },
              {
                tileReq: [TileFamily.HansaFalsatLertegel, TileFamily.HansaFalsatLertegelRakFramkant],
                arts: ['1903xx'],
              },
              {
                tileReq: [TileFamily.TvillingFalsatLertegel, TileFamily.TvillingLertegelRakFramkant],
                arts: ['1904xx'],
              },
              {
                tileReq: [TileFamily.PianoFalsatLertegel],
                arts: ['C838xx'],
              },
              {
                tileReq: [TileFamily.Strängpressat2kupLertegel],
                arts: ['1906xx'],
              },
              {
                tileReq: [TileFamily.Palema, TileFamily.Exklusiv,
                  TileFamily.HansaFalsatLertegel, TileFamily.HansaFalsatLertegelRakFramkant,
                  TileFamily.TvillingFalsatLertegel, TileFamily.TvillingLertegelRakFramkant],
                arts: ['170990', '170990-10', '1348xx', '1376xx'],
              },
              {
                tileReq: [TileFamily.Palema, TileFamily.Exklusiv, TileFamily.Carisma,
                  TileFamily.HansaFalsatLertegel, TileFamily.HansaFalsatLertegelRakFramkant,
                  TileFamily.PianoFalsatLertegel],
                fasadHöjdReq: [FacadeHeight.Less3m, FacadeHeight.Less4m],
                arts: ['0819xx'],
              },
              {
                tileReq: [TileFamily.Palema, TileFamily.Exklusiv, TileFamily.Carisma,
                  TileFamily.HansaFalsatLertegel, TileFamily.HansaFalsatLertegelRakFramkant,
                  TileFamily.TvillingFalsatLertegel, TileFamily.TvillingLertegelRakFramkant,
                  TileFamily.PianoFalsatLertegel],
                arts: ['1701xx', '1701xx-5', '1702xx', '1783xx', '1783xx-10', '1704xx', '1705xx', '1705xx-10',
                  '1706xx', '1707xx', '1708xx', '1713xx', '1714xx', '1715xx', '1722xx', '1724xx', '1726xx'],
              },
              { // BENT-209
                genericReq: !!koppladeTakstegArtNo,
                arts: [getArtPrefix(koppladeTakstegArtNo) + 'xx'],
              },
            ]
          },
          {
            marketReq: [MarketStr.Tyskland],
            arts: [],
            subRules: [
              {
                genericReq: [AccessoryColor.Black, AccessoryColor.GalvanizedGreySilver].includes(accessoryColor),
                arts: ['121120', '121020', '121620', '121720', '120120', '121320', '121420', '081720', '081920',
                  '081420', '114620', '831220', '081720', '081920'],
              },
              {
                genericReq: [AccessoryColor.BrickRed].includes(accessoryColor),
                arts: ['121124', '121024', '121624', '121724', '120124', '121324', '121424', '081724', '081924',
                  '081424', '114624', '831224', '081724', '081924'],
              },
              {
                tileReq: [TileFamily.Palema],
                arts: ['0732' + tileColorSuffix],
              },

              {
                tileReq: [TileFamily.Mecklenburger],
                arts: ['M732' + tileColorSuffix],
              },
              {
                tileReq: [TileFamily.Carisma],
                arts: ['C732' + tileColorSuffix],
              },
            ],
          },
        ];
        processRules(rules, whitelisted, tile.family, facadeHeight, angle, project.market);

        // Filter availableArticles on our whitelist. Some whitelisted articles might not appear if their article data isn't available
        const artMap: { [key: string]: string } = {};
        for (const art of availableArticles) {
          if (!art) continue;
          artMap[art] = art; // Whole article number mapping

          let endIndex = art.indexOf('-'); // xx:ed article number mapping
          if (endIndex == -1) endIndex = art.length;
          const strippedArt = art.substring(0, endIndex - 2) + art.substring(endIndex);
          artMap[strippedArt] = art;
        }

        items.push(...whitelisted.map((wlArt) => artMap[wlArt] ?? artMap[wlArt.replace('xx', '')]).filter((art) => !!art));

        // Some articles only exist with -90 postfix's, fetch those separately
        const fixedColorWhitelist = whitelisted.filter((artnr) => !artnr.includes('xx'));
        const fixedColorArticles = await getArticles(fixedColorWhitelist);
        items.push(...Object.keys(fixedColorArticles));

        break;
      case ExtraAssortment.Solar:
        if (project.market === MarketStr.Tyskland) {
          items.push('089791');
        }
        break;
    }
    items.push(
      ...calc_tile.value.getAccessoryOptions(key, project.market, {
        color: tile.color,
        finish: tile.coating,
        accessoryColor: accessoryColor,
      }),
    );
    acc[key] = items;
  }

  return acc;
};

/* 5. Extras watchers */
export default function () {
  // Update stored extras
  watch(
    () => selections.value.extra,
    (val) => {
      if (val) {
        const artnrs = [] as string[];
        for (const key in val) {
          for (const item of val[key] as unknown as {
            article: { article: Article };
            amount: { value: string };
            unit: { selected: string };
          }[]) {
            const amount = parseFloat(item.amount.value);
            if (item.article) {
              project.updateExtraArticle({
                artnr: item.article.article.ArtNr,
                amount: amount,
                total: (item.article.article.unit[item.unit.selected] ?? 1) * amount,
                unit: item.unit.selected,
              } as ExtraArticle);
              artnrs.push(item.article.article.ArtNr);
            }
          }
        }

        // project.cleanupExtraArticles(artnrs);
      }
    },
    { deep: true },
  );

  // Build extras
  const buildExtras = {
    abortController: null as AbortController | null,
    run: async function ([tile, angle, angle2, accessoryColor, baseroof, facadeHeight, loaded]: [
      Tile,
      number,
      number,
      AccessoryColor,
      Underlagstak,
      FacadeHeight,
      Promise<boolean>,
    ]) {
      // Abort the previous operation if it exists and start a new one
      this.abortController?.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      try {
        setLoading('extras', true);

        // Simulate an asynchronous operation
        const newValues = await new Promise<SectionValues>(async (resolve, reject) => {
          await loaded; // make sure variants are loaded

          const accessories = {} as { [key: string]: Article[] };

          const promises = [];
          const assortments = await getAssortments({
            tile,
            angle: project.isMansard(project.roofIndex) ? angle2 : angle,
            accessoryColor,
            baseroof,
            facadeHeight,
          });
          for (const key in assortments) {
            promises.push(
              getArticles(assortments[key].map((art) => (typeof art === 'string' ? art : art.artnr))).then((data) => {
                accessories[key] = Object.values(data);
              }),
            );
          }

          // wait for all promises
          await Promise.all(promises);

          const newValues = {} as SectionValues;
          for (const key in accessories) {
            newValues[key] = Object.values(accessories[key])
              .reduce((acc, cur) => {
                const prefix = getArtPrefix(cur.ArtNr); // Required for backwards compatibility
                const hash = getArtHash(cur.ArtNr);
                const existing = activeRoof.value.extras.articles?.[prefix] ?? activeRoof.value.extras.articles?.[hash];
                delete activeRoof.value.extras.articles?.[prefix];
                const artOptions = assortments[key].find((art) => typeof art !== 'string' && art.artnr == cur.ArtNr) as
                  | { artnr: string; sort: string }
                  | undefined;
                const item = {
                  article: {
                    article: cur,
                    total: 0,
                    weight: 0,
                  },
                  amount: {
                    value: existing?.amount?.toString() || '0',
                    type: 'number',
                    decimals: 0,
                  },
                  unit: {
                    article: cur,
                    selected: existing?.unit || cur.basicUnitOfMeasure || 'st',
                    options: Object.keys(cur?.unit ?? {}).map((unit) => ({
                      // label: convertUnit(unit),
                      value: unit,
                    })),
                  },
                  sort: artOptions?.sort || cur.ArtNr,
                };

                acc.push(item);
                return acc;
              }, [] as SectionValueItem[])
              .sort((a, b) => {
                const aName = a['sort'] as string;
                const bName = b['sort'] as string;
                if (aName < bName) {
                  return -1;
                }
                if (aName > bName) {
                  return 1;
                }
                return 0;
              });
          }

          resolve(newValues);
        });

        // Check if the operation has been aborted before setting the values
        if (!signal.aborted) {
          console.log('Updated assortments', newValues);
          selections.value.extra = newValues;
          setLoading('extras', false);
          this.abortController = null;
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error building extras', error);
        }
      }
    },
  };

  watch(
    () =>
      [
        activeRoof.value.tile,
        activeRoof.value.roof.dimensions.angle,
        activeRoof.value.roof.dimensions.angle2,
        activeRoof.value.accessories.color,
        activeRoof.value.roof.dimensions.baseRoof,
        activeRoof.value.roof.dimensions.facadeHeight,
        calc_tile.value.variantsLoaded,
      ] as [Tile, number, number, AccessoryColor, Underlagstak, FacadeHeight, Promise<boolean>],
    buildExtras.run.bind(buildExtras),
    { deep: true },
  );
}
